import { FormEvent, useEffect, useMemo, useState } from "react";
import { Ban, CalendarClock, ClipboardList, Copy, FilePlus2, Headphones, Pencil, Printer, RefreshCw, Save, Search, X } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import StoreLayout from "./StoreLayout";
import { preferredStoreLocationId, rememberedStoreLocationId } from "./storeLocationPreference";
import { copyPhoneNumber } from "./phoneActions";
import { hasStorePermission } from "./storePermissions";
import {
  createStoreServiceRecord,
  getCurrentStoreUser,
  getStoreServiceRecord,
  listStoreServiceRecords,
  logoutStore,
  searchStoreCustomers,
  searchStoreServiceProducts,
  StoreApiError,
  StoreCustomerSummary,
  StorePaginationMeta,
  StoreProductOption,
  StoreServiceRecord,
  StoreServiceRecordPayload,
  StoreUser,
  updateStoreServiceRecord,
  voidStoreServiceRecord,
} from "./api";

const defaultPagination: StorePaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };
const fallbackTypes = [
  { Key: "HearingConsult", Label: "聽力諮詢" },
  { Key: "HearingTest", Label: "聽力檢測" },
  { Key: "TrialFitting", Label: "試戴調整" },
  { Key: "Maintenance", Label: "保養維修" },
  { Key: "FollowUp", Label: "回訪追蹤" },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function currentYearStart() {
  return `${new Date().getFullYear()}-01-01`;
}

function currentYearEnd() {
  return `${new Date().getFullYear()}-12-31`;
}

function text(value?: string | number | null) {
  return value === undefined || value === null || value === "" ? "-" : String(value);
}

function earSideLabel(value?: string | null) {
  const labels: Record<string, string> = {
    Left: "左耳",
    Right: "右耳",
    Both: "雙耳",
    Accessory: "配件",
  };
  return value ? labels[value] || value : "-";
}

function escapeHtml(value?: string | number | null) {
  return text(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function nl2br(value?: string | null) {
  return escapeHtml(value).replaceAll("\n", "<br />");
}

function printDocument(title: string, body: string) {
  const popup = window.open("", "_blank", "noopener,noreferrer,width=980,height=720");
  if (!popup) {
    window.print();
    return;
  }
  popup.document.write(`<!doctype html>
<html lang="zh-Hant">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #f7faf8; color: #203d32; font-family: "Noto Sans TC", "Microsoft JhengHei", Arial, sans-serif; }
    .sheet { width: 210mm; min-height: 297mm; margin: 0 auto; background: #fff; padding: 18mm; }
    .header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 2px solid #315746; padding-bottom: 14px; }
    .brand { font-size: 22px; font-weight: 900; letter-spacing: .08em; }
    .sub { margin-top: 5px; color: #7b877f; font-size: 12px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; }
    .doc-title { text-align: right; font-size: 26px; font-weight: 900; }
    .doc-no { margin-top: 5px; color: #59665f; font-size: 13px; font-weight: 700; }
    .section { margin-top: 18px; }
    .section-title { margin: 0 0 8px; font-size: 15px; font-weight: 900; color: #315746; }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
    .field { border: 1px solid #dfe6e1; border-radius: 6px; padding: 8px 10px; min-height: 48px; }
    .label { color: #7b877f; font-size: 11px; font-weight: 800; }
    .value { margin-top: 4px; font-size: 14px; font-weight: 800; line-height: 1.5; word-break: break-word; }
    .note { border: 1px solid #dfe6e1; border-radius: 6px; padding: 10px; color: #59665f; font-size: 13px; line-height: 1.8; min-height: 72px; }
    .signatures { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; margin-top: 36px; }
    .sign { border-top: 1px solid #8b9891; padding-top: 8px; color: #59665f; font-size: 12px; font-weight: 800; text-align: center; }
    @page { size: A4; margin: 0; }
    @media print { body { background: #fff; } .sheet { width: auto; min-height: auto; margin: 0; padding: 14mm; } }
  </style>
</head>
<body><main class="sheet">${body}</main><script>window.onload = () => { window.focus(); window.print(); };</script></body>
</html>`);
  popup.document.close();
}

function serviceRecordPrintHtml(record: StoreServiceRecord) {
  return `
    <div class="header">
      <div><div class="brand">富聆聽健康</div><div class="sub">Store Portal</div></div>
      <div><div class="doc-title">門市服務紀錄</div><div class="doc-no">SR-${escapeHtml(record.ServiceRecordId)}</div></div>
    </div>
    <section class="section grid">
      <div class="field"><div class="label">客戶</div><div class="value">${escapeHtml(record.CustomerName)} / ${escapeHtml(record.CustomerPhone)}</div></div>
      <div class="field"><div class="label">門市 / 同仁</div><div class="value">${escapeHtml(record.LocationName)} / ${escapeHtml(record.StoreUserName)}</div></div>
      <div class="field"><div class="label">服務日期</div><div class="value">${escapeHtml(record.VisitDate)}</div></div>
      <div class="field"><div class="label">服務類型</div><div class="value">${escapeHtml(record.ServiceTypeLabel || record.ServiceType)}</div></div>
      <div class="field"><div class="label">試戴產品</div><div class="value">${escapeHtml(record.TrialProductName)}</div></div>
      <div class="field"><div class="label">機身序號 / 耳側</div><div class="value">${escapeHtml(record.TrialSerialNumber)} / ${escapeHtml(earSideLabel(record.TrialEarSide))}</div></div>
      <div class="field"><div class="label">下次回訪</div><div class="value">${escapeHtml(record.NextFollowUpAt)}</div></div>
      <div class="field"><div class="label">最後更新</div><div class="value">${escapeHtml(record.UpdatedAt || record.CreatedAt)}</div></div>
    </section>
    <section class="section"><h2 class="section-title">服務摘要</h2><div class="note">${nl2br(record.Summary)}</div></section>
    <section class="section"><h2 class="section-title">建議方案</h2><div class="note">${nl2br(record.Recommendation)}</div></section>
    ${record.Status === "Voided" ? `<section class="section"><h2 class="section-title">作廢原因</h2><div class="note">${nl2br(record.VoidReason)}</div></section>` : ""}
    <div class="signatures"><div class="sign">服務同仁</div><div class="sign">客戶確認</div><div class="sign">主管覆核</div></div>
  `;
}

function emptyForm(locationId = ""): StoreServiceRecordPayload {
  return {
    LocationId: locationId,
    ProductId: "",
    VariantId: "",
    CustomerName: "",
    CustomerPhone: "",
    VisitDate: today(),
    ServiceType: "HearingConsult",
    Summary: "",
    Recommendation: "",
    NextFollowUpAt: "",
    TrialProductName: "",
    TrialSerialNumber: "",
    TrialEarSide: "",
  };
}

function formFromRecord(record: StoreServiceRecord): StoreServiceRecordPayload {
  const trial = record.TrialRecords?.[0];
  return {
    LocationId: record.LocationId,
    ProductId: trial?.ProductId || "",
    VariantId: trial?.VariantId || "",
    CustomerName: record.CustomerName,
    CustomerPhone: record.CustomerPhone,
    VisitDate: record.VisitDate,
    ServiceType: record.ServiceType,
    Summary: record.Summary || "",
    Recommendation: record.Recommendation || "",
    NextFollowUpAt: record.NextFollowUpAt ? record.NextFollowUpAt.replace(" ", "T").slice(0, 16) : "",
    TrialProductName: record.TrialProductName || "",
    TrialSerialNumber: record.TrialSerialNumber || "",
    TrialEarSide: record.TrialEarSide || "",
    ReservationSourceType: record.ReservationSourceType || "",
    ReservationSourceId: record.ReservationSourceId || "",
  };
}

function InfoTile({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="min-w-0 rounded-md border border-[#edf0ee] bg-[#fbfcfb] px-3 py-2">
      <p className="text-xs font-bold text-[#7b877f]">{label}</p>
      <p className="mt-1 min-w-0 break-all text-sm font-bold leading-6 text-[#203d32]">{value || "-"}</p>
    </div>
  );
}

function RecordCard({ item, selected, onSelect }: { item: StoreServiceRecord; selected: boolean; onSelect: () => void }) {
  const isVoided = item.Status === "Voided";
  return (
    <button
      className={`w-full rounded-md border p-4 text-left transition hover:border-[#b8c8bf] hover:shadow-[0_8px_22px_rgba(32,61,50,.08)] ${
        selected ? "border-[#9ac8aa] ring-2 ring-[#9ac8aa]/20" : "border-[#dfe6e1]"
      } ${isVoided ? "bg-[#fff8f7] opacity-90" : "bg-white"}`}
      onClick={onSelect}
      type="button"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-base font-extrabold text-[#203d32]">{item.CustomerName}</p>
          <p className="mt-1 text-sm font-bold text-[#59665f]">{item.CustomerPhone}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${isVoided ? "bg-[#fdebea] text-[#9a2d2d]" : "bg-[#e9f4ec] text-[#245438]"}`}>
          {isVoided ? "已作廢" : item.ServiceTypeLabel || item.ServiceType}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-bold text-[#59665f]">
        <span className="rounded bg-[#f4f6f5] px-2 py-1">{item.VisitDate}</span>
        <span className="truncate rounded bg-[#f4f6f5] px-2 py-1">{item.TrialProductName || item.LocationName || "服務紀錄"}</span>
      </div>
    </button>
  );
}

export default function StoreServiceRecords() {
  const navigate = useNavigate();
  const { serviceRecordId } = useParams();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<StoreUser | null>(null);
  const [currentLocationId, setCurrentLocationId] = useState<string | null>(() => rememberedStoreLocationId());
  const [items, setItems] = useState<StoreServiceRecord[]>([]);
  const [record, setRecord] = useState<StoreServiceRecord | null>(null);
  const [pagination, setPagination] = useState<StorePaginationMeta>(defaultPagination);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [serviceTypes, setServiceTypes] = useState(fallbackTypes);
  const [keyword, setKeyword] = useState(searchParams.get("Keyword") || searchParams.get("keyword") || "");
  const [serviceType, setServiceType] = useState(searchParams.get("ServiceType") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("DateFrom") || currentYearStart());
  const [dateTo, setDateTo] = useState(searchParams.get("DateTo") || currentYearEnd());
  const [recordStatus, setRecordStatus] = useState<"Active" | "Voided" | "All">((searchParams.get("RecordStatus") as "Active" | "Voided" | "All" | null) || "Active");
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [form, setForm] = useState<StoreServiceRecordPayload>(emptyForm());
  const [formOpen, setFormOpen] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<StoreCustomerSummary[]>([]);
  const [productOptions, setProductOptions] = useState<StoreProductOption[]>([]);
  const [selectedCustomerKey, setSelectedCustomerKey] = useState<string | null>(null);
  const [selectedCustomerSnapshot, setSelectedCustomerSnapshot] = useState<{ Name: string; Phone: string } | null>(null);
  const [selectedProductKey, setSelectedProductKey] = useState<string | null>(null);
  const [selectedProductSnapshot, setSelectedProductSnapshot] = useState<string | null>(null);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [productSearching, setProductSearching] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<StoreServiceRecordPayload>(emptyForm());
  const [voidReason, setVoidReason] = useState("");
  const [voidPanelOpen, setVoidPanelOpen] = useState(false);

  const effectiveLocationId = useMemo(() => (
    preferredStoreLocationId(user, currentLocationId)
  ), [currentLocationId, user]);
  const canWriteServiceRecords = hasStorePermission(user, "store-service-records-write");
  const canVoidServiceRecords = hasStorePermission(user, "store-service-records-void");

  async function loadList(locationId?: string | null, page = pagination.Page) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentStoreUser();
      setUser(me.User);
      const nextLocationId = preferredStoreLocationId(me.User, locationId || currentLocationId);
      setCurrentLocationId(nextLocationId);
      setForm((current) => ({ ...current, LocationId: current.LocationId || nextLocationId || "" }));
      const data = await listStoreServiceRecords({ LocationId: nextLocationId, Keyword: keyword.trim(), ServiceType: serviceType, RecordStatus: recordStatus, DateFrom: dateFrom, DateTo: dateTo, IncludeVoided: recordStatus !== "Active", Page: page, PerPage: pagination.PerPage });
      setItems(data.ServiceRecords);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      if (data.ServiceTypeOptions?.length) setServiceTypes(data.ServiceTypeOptions);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "服務紀錄載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id?: string) {
    if (!id) {
      setRecord(null);
      return;
    }
    setDetailLoading(true);
    setMessage("");
    try {
      const data = await getStoreServiceRecord(id);
      if (effectiveLocationId && data.ServiceRecord.LocationId !== effectiveLocationId) {
        setRecord(null);
        setMessage("此服務紀錄不屬於目前選取門市，已回到服務紀錄列表。");
        navigate("/store/service-records", { replace: true });
        return;
      }
      setRecord(data.ServiceRecord);
      setEditMode(false);
      setVoidPanelOpen(false);
      setVoidReason("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "服務紀錄詳情載入失敗。");
      if (error instanceof StoreApiError && error.status === 401) navigate("/store-login", { replace: true });
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    loadList(null, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadDetail(serviceRecordId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRecordId, effectiveLocationId]);

  useEffect(() => {
    const query = form.CustomerPhone.trim() || form.CustomerName.trim();
    if (!effectiveLocationId || query.length < 2) {
      setCustomerOptions([]);
      return;
    }
    if (selectedCustomerKey && selectedCustomerSnapshot && selectedCustomerSnapshot.Name === form.CustomerName.trim() && selectedCustomerSnapshot.Phone === form.CustomerPhone.trim()) {
      setCustomerOptions([]);
      setCustomerSearching(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const data = await searchStoreCustomers({ LocationId: effectiveLocationId, Keyword: query, Page: 1, PerPage: 6 });
        setCustomerOptions(data.Customers || []);
      } catch {
        setCustomerOptions([]);
      } finally {
        setCustomerSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [effectiveLocationId, form.CustomerName, form.CustomerPhone, selectedCustomerKey, selectedCustomerSnapshot]);

  useEffect(() => {
    const query = (form.TrialProductName || "").trim();
    if (query.length < 1) {
      setProductOptions([]);
      return;
    }
    if (selectedProductKey && selectedProductSnapshot === query) {
      setProductOptions([]);
      setProductSearching(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      setProductSearching(true);
      try {
        const data = await searchStoreServiceProducts(query, 8);
        setProductOptions(data.Products || []);
      } catch {
        setProductOptions([]);
      } finally {
        setProductSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [form.TrialProductName, selectedProductKey, selectedProductSnapshot]);

  async function handleLogout() {
    await logoutStore().catch(() => undefined);
    navigate("/store-login", { replace: true });
  }

  function resetLocationScopedState(locationId: string) {
    setRecord(null);
    setForm(emptyForm(locationId));
    setFormOpen(false);
    setEditMode(false);
    setEditForm(emptyForm(locationId));
    setVoidPanelOpen(false);
    setVoidReason("");
    setCustomerOptions([]);
    setProductOptions([]);
    setSelectedCustomerKey(null);
    setSelectedCustomerSnapshot(null);
    setSelectedProductKey(null);
    setSelectedProductSnapshot(null);
  }

  function handleLocationChange(locationId: string) {
    setCurrentLocationId(locationId);
    resetLocationScopedState(locationId);
    navigate("/store/service-records");
    loadList(locationId, 1);
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    loadList(effectiveLocationId, 1);
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canWriteServiceRecords) {
      setMessage("此帳號沒有新增服務紀錄權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const payload = { ...form, LocationId: form.LocationId || effectiveLocationId || "" };
      const data = await createStoreServiceRecord(payload);
      setForm(emptyForm(effectiveLocationId || ""));
      setFormOpen(false);
      await loadList(effectiveLocationId, 1);
      if (data.ServiceRecord?.ServiceRecordId) navigate(`/store/service-records/${data.ServiceRecord.ServiceRecordId}`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "服務紀錄建立失敗。");
    } finally {
      setSaving(false);
    }
  }

  function updateForm(key: keyof StoreServiceRecordPayload, value: string) {
    if (key === "CustomerName" || key === "CustomerPhone") {
      setSelectedCustomerKey(null);
      setSelectedCustomerSnapshot(null);
    }
    if (key === "TrialProductName") {
      setSelectedProductKey(null);
      setSelectedProductSnapshot(null);
    }
    setForm((current) => ({
      ...current,
      [key]: value,
      ...(key === "TrialProductName" ? { ProductId: "", VariantId: "" } : {}),
    }));
  }

  function updateEditForm(key: keyof StoreServiceRecordPayload, value: string) {
    setEditForm((current) => ({
      ...current,
      [key]: value,
      ...(key === "TrialProductName" ? { ProductId: "", VariantId: "" } : {}),
    }));
  }

  function selectCustomer(item: StoreCustomerSummary) {
    setSelectedCustomerKey(item.CustomerKey);
    setSelectedCustomerSnapshot({ Name: item.Name, Phone: item.Phone });
    setForm((current) => ({ ...current, CustomerName: item.Name, CustomerPhone: item.Phone }));
    setCustomerOptions([]);
    setCustomerSearching(false);
  }

  function selectProduct(item: StoreProductOption) {
    setSelectedProductKey(`${item.ProductId}-${item.VariantId || "base"}`);
    setSelectedProductSnapshot(item.DisplayName.trim());
    setForm((current) => ({
      ...current,
      ProductId: item.ProductId,
      VariantId: item.VariantId || "",
      TrialProductName: item.DisplayName,
      ServiceType: current.ServiceType === "HearingConsult" ? "TrialFitting" : current.ServiceType,
    }));
    setProductOptions([]);
    setProductSearching(false);
  }

  function startCreate() {
    if (!canWriteServiceRecords) {
      setMessage("此帳號沒有新增服務紀錄權限。");
      return;
    }
    setForm(emptyForm(effectiveLocationId || ""));
    setCustomerOptions([]);
    setProductOptions([]);
    setSelectedCustomerKey(null);
    setSelectedCustomerSnapshot(null);
    setSelectedProductKey(null);
    setSelectedProductSnapshot(null);
    setEditMode(false);
    setVoidPanelOpen(false);
    setFormOpen(true);
  }

  function cancelCreate() {
    setForm(emptyForm(effectiveLocationId || ""));
    setCustomerOptions([]);
    setProductOptions([]);
    setSelectedCustomerKey(null);
    setSelectedCustomerSnapshot(null);
    setSelectedProductKey(null);
    setSelectedProductSnapshot(null);
    setFormOpen(false);
  }

  function startEdit() {
    if (!record) return;
    if (!canWriteServiceRecords) {
      setMessage("此帳號沒有編輯服務紀錄權限。");
      return;
    }
    setEditForm(formFromRecord(record));
    setEditMode(true);
    setVoidPanelOpen(false);
  }

  async function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!record) return;
    if (!canWriteServiceRecords) {
      setMessage("此帳號沒有編輯服務紀錄權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const data = await updateStoreServiceRecord(record.ServiceRecordId, editForm);
      setRecord(data.ServiceRecord);
      setEditMode(false);
      await loadList(effectiveLocationId, pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "服務紀錄更新失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleVoid() {
    if (!record) return;
    if (!canVoidServiceRecords) {
      setMessage("此帳號沒有作廢服務紀錄權限。");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const data = await voidStoreServiceRecord(record.ServiceRecordId, voidReason);
      setRecord(data.ServiceRecord);
      setVoidPanelOpen(false);
      setEditMode(false);
      setVoidReason("");
      await loadList(effectiveLocationId, 1);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "服務紀錄作廢失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <StoreLayout currentLocationId={effectiveLocationId} onLocationChange={handleLocationChange} onLogout={handleLogout} title="服務紀錄" user={user}>
      <div className="space-y-4">
        <form className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 2xl:grid-cols-[minmax(300px,1fr)_minmax(170px,190px)_minmax(150px,170px)_minmax(180px,200px)_minmax(180px,200px)_auto_auto] 2xl:items-end" onSubmit={handleSearch}>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="service-keyword">關鍵字</label>
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="service-keyword" placeholder="姓名、電話、試戴產品" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="service-type">服務類型</label>
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="service-type" value={serviceType} onChange={(event) => setServiceType(event.target.value)}>
              <option value="">全部類型</option>
              {serviceTypes.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="record-status">狀態</label>
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="record-status" value={recordStatus} onChange={(event) => setRecordStatus(event.target.value as "Active" | "Voided" | "All")}>
              <option value="Active">有效</option>
              <option value="Voided">已作廢</option>
              <option value="All">全部</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="service-from">起日</label>
            <input className="h-11 w-full min-w-[180px] rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="service-from" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-bold text-[#59665f]" htmlFor="service-to">迄日</label>
            <input className="h-11 w-full min-w-[180px] rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="service-to" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </div>
          <button className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => loadList(effectiveLocationId, pagination.Page)} type="button"><RefreshCw size={18} />重新整理</button>
        </form>

        {message && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

        <div className="grid gap-3 sm:grid-cols-3">
          <InfoTile label="總服務紀錄" value={`${counts.Total || 0} 筆`} />
          <InfoTile label="今日服務" value={`${counts.Today || 0} 筆`} />
          <InfoTile label="已作廢" value={`${counts.Voided || 0} 筆`} />
        </div>

        <div className="grid gap-4 2xl:grid-cols-[minmax(320px,440px)_minmax(0,1fr)]">
          <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-3">
            <div className="mb-3 flex items-center justify-between px-1">
              <div>
                <h2 className="font-extrabold text-[#203d32]">服務紀錄列表</h2>
                <p className="mt-1 text-sm text-[#7b877f]">共 {pagination.Total} 筆</p>
              </div>
              <ClipboardList className="text-[#315746]" size={20} />
            </div>
            {loading ? (
              <EmptyText text="載入中..." />
            ) : items.length === 0 ? (
              <EmptyText text="目前沒有符合條件的服務紀錄。" />
            ) : (
              <div className="grid gap-3">
                {items.map((item) => (
                  <RecordCard
                    item={item}
                    key={item.ServiceRecordId}
                    selected={item.ServiceRecordId === serviceRecordId}
                    onSelect={() => {
                      if (!editMode) setFormOpen(false);
                      navigate(`/store/service-records/${item.ServiceRecordId}`);
                    }}
                  />
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-col gap-2 border-t border-[#edf0ee] pt-3 sm:flex-row sm:items-center sm:justify-between">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page <= 1 || loading} onClick={() => loadList(effectiveLocationId, pagination.Page - 1)} type="button">上一頁</button>
              <span className="text-center text-sm font-bold text-[#59665f]">{pagination.Page} / {pagination.TotalPages}</span>
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={pagination.Page >= pagination.TotalPages || loading} onClick={() => loadList(effectiveLocationId, pagination.Page + 1)} type="button">下一頁</button>
            </div>
          </section>

          <section className="min-w-0 space-y-4">
            {!formOpen ? (
              <div className="flex flex-col gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="flex items-center gap-2 font-extrabold text-[#203d32]"><FilePlus2 size={18} />服務紀錄</h2>
                  <p className="mt-1 text-sm font-medium text-[#7b877f]">選擇左側紀錄查看詳情；需要新增服務內容時再展開表單。</p>
                </div>
                {canWriteServiceRecords ? (
                  <button className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] md:w-auto" onClick={startCreate} type="button">
                    <FilePlus2 size={18} />新增服務紀錄
                  </button>
                ) : (
                  <span className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] px-4 py-3 text-sm font-bold text-[#7b877f]">僅可查看服務紀錄</span>
                )}
              </div>
            ) : (
            <form className="rounded-md border border-[#dfe6e1] bg-white p-4" onSubmit={handleCreate}>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="flex items-center gap-2 font-extrabold text-[#203d32]"><FilePlus2 size={18} />新增服務紀錄</h2>
                <div className="rwd-action-grid">
                  <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={cancelCreate} type="button"><X size={16} />取消新增</button>
                  <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 text-sm font-bold text-white disabled:opacity-60" disabled={saving || !canWriteServiceRecords} type="submit"><Save size={16} />{saving ? "儲存中" : "儲存"}</button>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="relative md:col-span-2 xl:col-span-2">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input label="客戶姓名" value={form.CustomerName} onChange={(value) => updateForm("CustomerName", value)} />
                    <Input label="客戶電話" value={form.CustomerPhone} onChange={(value) => updateForm("CustomerPhone", value)} />
                  </div>
                  {(customerOptions.length > 0 || customerSearching) && (
                    <div className="absolute left-0 right-0 top-[72px] z-20 rounded-md border border-[#dfe6e1] bg-white p-2 shadow-[0_16px_36px_rgba(32,61,50,.14)]">
                      {customerSearching && <div className="px-3 py-2 text-sm font-bold text-[#7b877f]">搜尋客戶中...</div>}
                      {customerOptions.map((item) => (
                        <button className="flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left hover:bg-[#f0f5f2]" key={item.CustomerKey} onClick={() => selectCustomer(item)} type="button">
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-extrabold text-[#203d32]">{item.Name}</span>
                            <span className="block truncate text-xs font-bold text-[#7b877f]">{item.Phone}{item.Email ? ` / ${item.Email}` : ""}</span>
                          </span>
                          <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${item.IsMember ? "bg-[#fff2d8] text-[#8a4c00]" : "bg-[#e9f4ec] text-[#245438]"}`}>
                            {item.IsMember ? "系統會員" : `${item.ReservationCount} 預約`}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input label="服務日期" type="date" value={form.VisitDate} onChange={(value) => updateForm("VisitDate", value)} />
                <div>
                  <label className="mb-1 block text-sm font-bold text-[#59665f]">服務類型</label>
                  <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.ServiceType} onChange={(event) => updateForm("ServiceType", event.target.value)}>
                    {serviceTypes.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <TextArea label="服務摘要" placeholder="例如：右耳配戴回饋偏尖銳，調整高頻增益後回饋改善。" value={form.Summary || ""} onChange={(value) => updateForm("Summary", value)} />
                <TextArea label="建議方案" placeholder="例如：建議試戴一週後回店微調，並安排下次追蹤。" value={form.Recommendation || ""} onChange={(value) => updateForm("Recommendation", value)} />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className="relative">
                  <Input label="試戴產品" value={form.TrialProductName || ""} onChange={(value) => updateForm("TrialProductName", value)} />
                  {(productOptions.length > 0 || productSearching) && (
                    <div className="absolute left-0 right-0 top-[72px] z-20 max-h-[280px] overflow-y-auto rounded-md border border-[#dfe6e1] bg-white p-2 shadow-[0_16px_36px_rgba(32,61,50,.14)]">
                      {productSearching && <div className="px-3 py-2 text-sm font-bold text-[#7b877f]">搜尋商品中...</div>}
                      {productOptions.map((item) => (
                        <button className="w-full rounded px-3 py-2 text-left hover:bg-[#f0f5f2]" key={`${item.ProductId}-${item.VariantId || "base"}`} onClick={() => selectProduct(item)} type="button">
                          <span className="block truncate text-sm font-extrabold text-[#203d32]">{item.DisplayName}</span>
                          <span className="mt-1 flex flex-wrap gap-2 text-xs font-bold text-[#7b877f]">
                            <span>{item.Sku}</span>
                            <span>NT$ {Number(item.Price || 0).toLocaleString()}</span>
                            <span>庫存 {item.Stock ?? "-"}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Input label="機身序號" value={form.TrialSerialNumber || ""} onChange={(value) => updateForm("TrialSerialNumber", value)} />
                <div>
                  <label className="mb-1 block text-sm font-bold text-[#59665f]">耳側</label>
                  <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.TrialEarSide || ""} onChange={(event) => updateForm("TrialEarSide", event.target.value)}>
                    <option value="">未指定</option>
                    <option value="Left">左耳</option>
                    <option value="Right">右耳</option>
                    <option value="Both">雙耳</option>
                  </select>
                </div>
                <Input label="下次回訪" type="datetime-local" value={form.NextFollowUpAt || ""} onChange={(value) => updateForm("NextFollowUpAt", value)} />
              </div>
            </form>
            )}

            <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
              {!serviceRecordId ? (
                <div className="flex min-h-[260px] flex-col items-center justify-center rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 text-center">
                  <Headphones className="mb-3 text-[#315746]" size={34} />
                  <h2 className="text-xl font-extrabold text-[#203d32]">選擇一筆服務紀錄查看詳情</h2>
                </div>
              ) : detailLoading ? (
                <EmptyText text="詳情載入中..." />
              ) : record ? (
                <div className="space-y-4">
                  <div className="flex flex-col gap-3 border-b border-[#edf0ee] pb-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#e9f4ec] px-2.5 py-1 text-xs font-bold text-[#245438]">{record.ServiceTypeLabel || record.ServiceType}</span>
                        <span className="rounded-full bg-[#f4f6f5] px-2.5 py-1 text-xs font-bold text-[#59665f]">{record.LocationName || "門市"}</span>
                        {record.Status === "Voided" && <span className="rounded-full bg-[#fdebea] px-2.5 py-1 text-xs font-bold text-[#9a2d2d]">已作廢</span>}
                      </div>
                      <h2 className="text-2xl font-extrabold text-[#203d32]">{record.CustomerName}</h2>
                      <p className="mt-1 text-sm text-[#59665f]">{record.CustomerPhone}</p>
                    </div>
                    <div className="rwd-action-grid">
                      <button className="inline-flex h-11 min-w-[120px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 font-bold text-white" onClick={() => copyPhoneNumber(record.CustomerPhone)} type="button"><Copy size={18} />複製電話</button>
                      <button className="inline-flex h-11 min-w-[120px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => printDocument(`服務紀錄-${record.ServiceRecordId}`, serviceRecordPrintHtml(record))} type="button"><Printer size={18} />列印紀錄</button>
                      {record.Status !== "Voided" && (canWriteServiceRecords || canVoidServiceRecords) && (
                        <>
                          {canWriteServiceRecords && <button className="inline-flex h-11 min-w-[88px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={startEdit} type="button"><Pencil size={18} />編輯</button>}
                          {canVoidServiceRecords && <button className="inline-flex h-11 min-w-[88px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#f2c9c6] bg-white px-4 font-bold text-[#9a2d2d] hover:bg-[#fdebea]" onClick={() => { setVoidPanelOpen(true); setEditMode(false); }} type="button"><Ban size={18} />作廢</button>}
                        </>
                      )}
                    </div>
                  </div>
                  {voidPanelOpen && (
                    <div className="rounded-md border border-[#f2c9c6] bg-[#fff8f7] p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h3 className="font-extrabold text-[#9a2d2d]">作廢服務紀錄</h3>
                        <button className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#e8c8c5] bg-white text-[#9a2d2d]" onClick={() => setVoidPanelOpen(false)} type="button"><X size={16} /></button>
                      </div>
                      <label className="mb-1 block text-sm font-bold text-[#59665f]">作廢原因</label>
                      <textarea className="min-h-[92px] w-full rounded-md border border-[#e8c8c5] p-3 outline-none focus:border-[#9a2d2d] focus:ring-2 focus:ring-[#9a2d2d]/15" placeholder="例如：建錯客戶、重複建立、測試資料。" value={voidReason} onChange={(event) => setVoidReason(event.target.value)} />
                      <div className="mt-3 rwd-action-grid sm:flex sm:justify-end">
                        <button className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-md bg-[#9a2d2d] px-4 text-sm font-bold text-white disabled:opacity-60" disabled={saving || voidReason.trim() === ""} onClick={handleVoid} type="button"><Ban size={16} />確認作廢</button>
                      </div>
                    </div>
                  )}
                  {editMode ? (
                    <form className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" onSubmit={handleUpdate}>
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <h3 className="font-extrabold text-[#203d32]">編輯服務紀錄</h3>
                        <div className="rwd-action-grid">
                          <button className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 text-sm font-bold text-[#315746]" onClick={() => setEditMode(false)} type="button"><X size={16} />取消</button>
                          <button className="inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-4 text-sm font-bold text-white disabled:opacity-60" disabled={saving || !canWriteServiceRecords} type="submit"><Save size={16} />儲存修改</button>
                        </div>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <Input label="客戶姓名" value={editForm.CustomerName} onChange={(value) => updateEditForm("CustomerName", value)} />
                        <Input label="客戶電話" value={editForm.CustomerPhone} onChange={(value) => updateEditForm("CustomerPhone", value)} />
                        <Input label="服務日期" type="date" value={editForm.VisitDate} onChange={(value) => updateEditForm("VisitDate", value)} />
                        <div>
                          <label className="mb-1 block text-sm font-bold text-[#59665f]">服務類型</label>
                          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={editForm.ServiceType} onChange={(event) => updateEditForm("ServiceType", event.target.value)}>
                            {serviceTypes.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="mt-3 grid gap-3 lg:grid-cols-2">
                        <TextArea label="服務摘要" placeholder="輸入本次服務內容。" value={editForm.Summary || ""} onChange={(value) => updateEditForm("Summary", value)} />
                        <TextArea label="建議方案" placeholder="輸入後續建議、追蹤或調整方向。" value={editForm.Recommendation || ""} onChange={(value) => updateEditForm("Recommendation", value)} />
                      </div>
                      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <Input label="試戴產品" value={editForm.TrialProductName || ""} onChange={(value) => updateEditForm("TrialProductName", value)} />
                        <Input label="機身序號" value={editForm.TrialSerialNumber || ""} onChange={(value) => updateEditForm("TrialSerialNumber", value)} />
                        <div>
                          <label className="mb-1 block text-sm font-bold text-[#59665f]">耳側</label>
                          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={editForm.TrialEarSide || ""} onChange={(event) => updateEditForm("TrialEarSide", event.target.value)}>
                            <option value="">未指定</option>
                            <option value="Left">左耳</option>
                            <option value="Right">右耳</option>
                            <option value="Both">雙耳</option>
                          </select>
                        </div>
                        <Input label="下次回訪" type="datetime-local" value={editForm.NextFollowUpAt || ""} onChange={(value) => updateEditForm("NextFollowUpAt", value)} />
                      </div>
                    </form>
                  ) : (
                    <>
                      {record.Status === "Voided" && <div className="rounded-md border border-[#f2c9c6] bg-[#fff8f7] px-4 py-3 text-sm font-bold text-[#9a2d2d]">作廢原因：{record.VoidReason || "-"}</div>}
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <InfoTile label="服務日期" value={record.VisitDate} />
                        <InfoTile label="服務同仁" value={record.StoreUserName} />
                        <InfoTile label="試戴產品" value={record.TrialProductName} />
                        <InfoTile label="下次回訪" value={record.NextFollowUpAt} />
                      </div>
                      <TextBlock icon={<Headphones size={18} />} title="服務摘要" text={record.Summary} />
                      <TextBlock icon={<CalendarClock size={18} />} title="建議方案" text={record.Recommendation} />
                      <div className="grid gap-3 md:grid-cols-3">
                        <InfoTile label="機身序號" value={record.TrialSerialNumber} />
                        <InfoTile label="耳側" value={earSideLabel(record.TrialEarSide)} />
                        <InfoTile label="最後更新" value={record.UpdatedAt} />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <EmptyText text="找不到服務紀錄。" />
              )}
            </section>
          </section>
        </div>
      </div>
    </StoreLayout>
  );
}

function Input({ label, onChange, type = "text", value }: { label: string; onChange: (value: string) => void; type?: string; value: string }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-[#59665f]">{label}</label>
      <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function TextArea({ label, onChange, placeholder, value }: { label: string; onChange: (value: string) => void; placeholder: string; value: string }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-[#59665f]">{label}</label>
      <textarea className="min-h-[112px] w-full rounded-md border border-[#cfd8d2] p-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function TextBlock({ icon, text, title }: { icon: React.ReactNode; text?: string | null; title: string }) {
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
      <h3 className="mb-3 flex items-center gap-2 font-extrabold text-[#203d32]">{icon}{title}</h3>
      <p className="whitespace-pre-wrap text-sm leading-6 text-[#59665f]">{text || "-"}</p>
    </section>
  );
}

function EmptyText({ text }: { text: string }) {
  return <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#f7faf8] px-4 py-8 text-center text-sm text-[#7b877f]">{text}</div>;
}
